import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import {
  getCountryCode,
  getLanguageCode,
  localizedShopPath,
} from 'utilities/locales';
import { listRecommendedProducts } from '../../services/shop_service';
import PageLoader from 'components/common/page_loader';
import { CSSTransition } from 'react-transition-group';
import { MarketDivider, MarketLink } from '@market/react';
import RecommendedProducts from 'components/common/recommended_products';
import {
  trackCdp,
  trackClickFeature,
  trackViewFeature,
} from 'utilities/tracking';
import { Locale } from '../../rpc/model/squareup/solidshop/v2/common/locale';
import { isDashboardMode } from 'helpers/search_parameter_helpers';
import { COMPONENT_NAMES } from 'constants/tracking';

const useFadeStyles = makeStyles({
  appear: { opacity: 0 },
  exit: { opacity: 0 },
  exitActive: { opacity: 0 },
});

const TrackingEventTypes = {
  AddToCart: 'AddToCart',
  ViewPdp: 'ViewPdp',
  ClickAllAccessoriesLink: 'ClickAllAccessoriesLink',
};

const RecommendedProductsModule = (props) => {
  const { orderData, setToast, orderRefresh, cartTrackingData } = props;
  const { t } = useTranslation();

  const [recommendedProductsData, setRecommendedProductsData] = useState(null);
  const [oldCartKeysCount, setOldCartKeysCount] = useState(0);

  const accessoriesLink = `${localizedShopPath()}/products/accessories`;
  const fadeClasses = useFadeStyles();

  const buildTrackingParams = ({ slug, name, id }) => {
    if (!slug && !name && !id) return {};

    return {
      cart: cartTrackingData,
      ...(slug && name && id
        ? {
            target_product: {
              id,
              name,
              slug,
            },
          }
        : {}),
    };
  };

  const trackViewEvent = ({ eventName, slug, name, id, eventDescription }) => {
    if (!orderData || !cartTrackingData) return;

    trackCdp({
      name: `RecommendedProducts:${eventName}`,
      params: {
        feature: 'shop',
        ...buildTrackingParams({ slug, name, id }),
      },
    });

    trackViewFeature({
      componentName: COMPONENT_NAMES.APPS.CART.RECOMMENDED_FOR_YOU,
      eventDescription,
      additionalParameters: buildTrackingParams({ slug, name, id }),
    });
  };

  const trackClickEvent = ({
    eventName,
    slug,
    name,
    id,
    eventDescription,
    actionItem,
    subActionItem,
  }) => {
    if (!orderData || !cartTrackingData) return;

    trackCdp({
      name: `RecommendedProducts:${eventName}`,
      params: {
        feature: 'shop',
        ...buildTrackingParams({ slug, name, id }),
      },
    });

    trackClickFeature({
      componentName: COMPONENT_NAMES.APPS.CART.RECOMMENDED_FOR_YOU,
      eventDescription,
      actionItem,
      subActionItem,
      additionalParameters: buildTrackingParams({ slug, name, id }),
    });
  };

  const onAccessoriesLinkClick = () => {
    trackClickEvent({
      eventName: TrackingEventTypes.ClickAllAccessoriesLink,
      eventDescription: 'View all accessories page',
      actionItem: 'shop_all_accessories',
    });
  };

  const fetchRecommendedProducts = () => {
    if (!orderData) return;

    const cartProductKeys = orderData.cart_summary.map(
      (product) => product.product_key,
    );

    /*
     Cache stops requests after products are added to cart. Reload recommendations when
     products are removed as the product removed may be the hero product (the product
     in which recommendations are based on.)
    */
    if (cartProductKeys.length === oldCartKeysCount) {
      return;
    }

    const storeLocale =
      `${getLanguageCode()}_${getCountryCode()}`.toUpperCase();

    listRecommendedProducts({
      productKeys: cartProductKeys,
      locale: Locale[storeLocale],
      storeCode: getCountryCode().toUpperCase(),
    }).then((data) => {
      setRecommendedProductsData(data.products);
      setOldCartKeysCount(cartProductKeys.length);
    });
  };

  const filteredOutInCartRecommendedProducts = (products) => {
    if (!orderData) return products;

    const cartProductKeys = new Set(
      orderData.cart_summary.map((product) => product.product_key),
    );

    return products.filter(
      (product) => !cartProductKeys.has(product.productKey),
    );
  };

  useEffect(() => {
    fetchRecommendedProducts();
  }, [orderData]);

  /*
   As a product requirement, we should show at least 2 recommendations at a time.
   And we will have no recommendations if the cart is empty
  */
  if (recommendedProductsData?.length < 2 || !orderData?.cart_summary?.length) {
    return null;
  }

  if (!recommendedProductsData) {
    return (
      <Box sx={{ pt: 5 }}>
        <PageLoader />
      </Box>
    );
  }

  return (
    <CSSTransition
      in={recommendedProductsData !== null}
      timeout={500}
      classNames={fadeClasses}
      appear
    >
      <Box
        sx={{
          transitionDuration: '150ms',
          transitionTimingFunction: 'ease-out',
        }}
      >
        <MarketDivider />
        <Box
          component="h2"
          sx={{
            mb: 1,
          }}
        >
          {t('cart.recommendedProducts.title')}
        </Box>
        {!isDashboardMode() && (
          <MarketLink href={accessoriesLink} onClick={onAccessoriesLinkClick}>
            {`${t('cart.recommendedProducts.cta')} ->`}
          </MarketLink>
        )}
        <RecommendedProducts
          products={filteredOutInCartRecommendedProducts(
            recommendedProductsData,
          )}
          orderRefresh={orderRefresh}
          setRecommendedProductsData={setRecommendedProductsData}
          setToast={setToast}
          trackViewEvent={trackViewEvent}
          trackClickEvent={trackClickEvent}
          trackingEventTypes={TrackingEventTypes}
        />
      </Box>
    </CSSTransition>
  );
};

export default RecommendedProductsModule;
